import { useCallback, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import { createFetchWebEventCollector, type Currency, EventName, PageName, type Predecessor } from 'web-event-collector'
import { getEnv } from '@unni/platform'

import { SearchServiceOffer, SearchServiceOfferItem, SearchPaginationSort } from '@/features/search/api/search.types'
import { useSearchServiceOffers } from '@/features/service-offer/api/service-offer.queries'
import EventListTemplate from '@/components/atomic/templates/EventListTemplate'
import TopDownloadScreen from '@/components/atomic/templates/TopDownloadScreen'
import { postAmplitude, postGA } from '@/core/lib'
import { createCommonEvent } from '@/utils/eventCollector'
import ResponsiveLayout from '@/components/atomic/templates/ResponsiveLayout'
import { Countries, getSearchDistrictCodes, Locales, type PathnamePrefix } from '@/utils/language'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { SearchService } from '@/features/search/api/search.service'
import { getServerTranslation } from '@/utils/i18n/i18n-server'
import { User } from '@/features/member/member.type'
import { ServiceOfferCategory } from '@/features/treatment-category-filter/components/service-offer-category-filter'
import { ServiceOfferSearchFilter } from '@/features/service-offer/components/service-offer-search-filter'
import { TreatmentFilterResponseController } from '@/features/treatment-category-filter/api'
import SortingDropdown from '@/components/atomic/molecules/SortingDropdown'
import {
  type DistrictsResponse,
  DistrictResponseController,
  getAllDistricts,
} from '@/features/district-filter/api/get-districts'
import { getHospitalProductJsonLdWithContext } from '@/utils/scheme'
import { JsonLd } from '@/components'
import ContextEnhancer from '@/shared/libs/context-enhancer'
import { getDefaultMeta, type SEOMeta } from '@/shared/utils'
import axios, { type AxiosRequestHeaders } from 'axios'

import { TreatmentCategoryFilterService } from '@/features/treatment-category-filter/api/treatment-category-filter.service'
import { createSolarApiClient } from '@/shared/libs/api-client'

import type { EventListItemOnClickEventParam } from '@/components/pages/eventList/EventListItem'
import type { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next'
import type {
  TreatmentCategory,
  TreatmentFilterResponse,
} from '@/features/treatment-category-filter/types/treatment-filter.type'

interface EventListPageProps {
  user: User | null
  data: {
    serviceOfferCategory: TreatmentFilterResponse
    serviceOffers: SearchServiceOfferItem[]
    serviceOfferPagination: {
      start: number
      length: number
      recordsTotal: number
      recordsFiltered: number
    }
    defaultText: {
      discountedCostText: string
    }
    selectedDistrictName?: string
  }
  token: string
  locale: Locales
  query: {
    country: Countries
    keyword: string
    category: string
    q: string
    district: string
    categoryId: string
    s: SearchPaginationSort
    filters: {
      hospital: {
        district: {
          neighborhoodVicinityCodes: string[]
        }
      }
    }
  }
  meta: SEOMeta
}

const EventListPage: NextPage<EventListPageProps> = ({ user, data, locale, query, token }) => {
  const { q, district, keyword, filters, categoryId, s: sortParam } = query
  const router = useRouter()
  const currentSort = sortParam || 'RECOMMENDATION'

  const searchParams = useMemo(
    () => ({
      headers: {
        authorization: token || '',
        'Accept-Language': locale,
      },
      keyword,
      filters,
      sort: currentSort,
      pageSize: 20,
    }),
    [token, locale, keyword, filters, currentSort],
  )

  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useSearchServiceOffers({
    params: {
      ...searchParams,
      headers: {
        ...searchParams.headers,
        Authorization: searchParams.headers.authorization,
      },
    },
    options: {
      initialData: {
        pages: [
          {
            contents: data.serviceOffers,
            currentPageIndex: 0,
            totalElementNumber: data.serviceOfferPagination.recordsTotal,
            totalPageNumber: Math.ceil(data.serviceOfferPagination.recordsTotal / 20),
            pageSize: 20,
            isLastPage: data.serviceOffers.length >= data.serviceOfferPagination.recordsTotal,
          },
        ],
        pageParams: [0],
      },
    },
  })

  const serviceOffers = useMemo(() => {
    if (!queryData?.pages) return []
    return queryData.pages.flatMap((page) => page.contents)
  }, [queryData])

  const total = queryData?.pages?.[0]?.totalElementNumber || data.serviceOfferPagination.recordsTotal

  const loadData = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleClickMoreButton = useCallback(() => {
    loadData()

    postAmplitude('click_event_more', {
      district_name: data.selectedDistrictName || '',
      district_code: district || '',
      category_id: categoryId || '',
      category_name: categoryId ? q : '',
      q: q || '',
      keyword: keyword || '',
    })
  }, [data.selectedDistrictName, district, categoryId, q, keyword, loadData])

  const handleReachBottom = useCallback(() => {
    loadData()
  }, [loadData])

  const handleSortChange = useCallback(
    (newSort: SearchPaginationSort) => {
      const query = { ...router.query }
      if (newSort === 'RECOMMENDATION') {
        delete query.s
      } else {
        query.s = newSort
      }
      router.push({ pathname: router.pathname, query }, undefined, { shallow: false })
    },
    [router],
  )

  const { emitServiceOfferClicked, emitServiceOfferViewed } = createFetchWebEventCollector({
    baseURL: getEnv('NEXT_PUBLIC_SOLAR_API_HOST') || '',
  })

  const storePredecessorAndEmitEvent = ({
    event,
    userId,
    index,
  }: {
    event: {
      id: number
      title: string
      cost: number
      discountedCost: number
      discountRate: number
      currency: string
      hospitalId: number
    }
    userId: number
    index: number
  }) => {
    const commonEvent = createCommonEvent(navigator.userAgent, userId)

    const predecessor: Predecessor = {
      Name: EventName.ServiceOfferClicked,
      TraceId: commonEvent.TraceId,
    }

    localStorage.setItem('predecessor', JSON.stringify(predecessor))

    emitServiceOfferClicked({
      ...commonEvent,
      Name: EventName.ServiceOfferClicked,
      Body: {
        ...commonEvent.Body,
        hospitalId: event.hospitalId,
        serviceOfferId: event.id,
        serviceOfferType: 'SPECIAL_OFFER',
        sellingPriceCurrency: event.currency as Currency,
        sellingPriceAmount: event.cost,
        itemOrderInPage: index + 1,
        pageName: PageName.EventTotalWeb,
      },
    })
  }

  const handleClickEvent = useCallback(
    (event: EventListItemOnClickEventParam, index: number) => {
      if (user && user.id !== 0) {
        storePredecessorAndEmitEvent({
          event: {
            id: event.id,
            title: event.title,
            cost: event.cost,
            discountedCost: event.discountedCost,
            discountRate: event.discountRate,
            currency: event.currency,
            hospitalId: event.hospitalId,
          },
          userId: user.id,
          index,
        })
      }

      postAmplitude('click_event_on_list', {
        event_id: event.id,
        event_title: event.title,
        cost: event.cost,
        discountedCost: event.discountedCost,
        discount_rate: event.discountRate,
      })
    },
    [user],
  )

  const handleImpressedEvent = useCallback(
    async (event: SearchServiceOffer, index: number) => {
      if (user && user.id !== 0) {
        const commonEvent = createCommonEvent(navigator.userAgent, user.id)

        emitServiceOfferViewed({
          ...commonEvent,
          Name: EventName.ServiceOfferViewed,
          Body: {
            ...commonEvent.Body,
            hospitalId: event.hospital.id,
            serviceOfferId: event.id,
            serviceOfferType: 'SPECIAL_OFFER',
            itemOrderInPage: index + 1,
            sellingPriceAmount: event.representativeOption.price.originalAmount,
            sellingPriceCurrency: event.representativeOption.price.currency as Currency,
            pageName: PageName.EventTotalWeb,
          },
        })
      }
    },
    [user, emitServiceOfferViewed],
  )

  useEffect(() => {
    if (user && user.id !== 0) {
      sessionStorage.setItem('user_id', user.id.toString())
    } else {
      sessionStorage.removeItem('user_id')
    }
  }, [user])

  useEffect(() => {
    postAmplitude('view_event_list', {
      q: query.q || '',
      keyword: query.category || query.q || '',
      district_code: query.district || '',
      district_name: data.selectedDistrictName || '',
      category_id: query.categoryId || '',
      category_name: query.categoryId ? query.q : '',
    })
    postGA('view_hospital_list', 'pageview')
  }, [query, data.selectedDistrictName])

  useEffect(() => {
    sendAirbridgeEvent(AirbridgeEvents.EVENT_VIEW, {})
  }, [])

  return (
    <ResponsiveLayout>
      <TopDownloadScreen userProfileImage={user?.profileImage} useGnb={true} selectedGnbTabKey="event">
        <ServiceOfferCategory defaultTreatmentCategories={data.serviceOfferCategory.treatmentCategories}>
          <ServiceOfferCategory.TreatmentCategoryList />
          <ServiceOfferCategory.TreatmentGroupList />
          <ServiceOfferCategory.TreatmentList />
        </ServiceOfferCategory>
        <ServiceOfferSearchFilter
          sortingComponent={<SortingDropdown currentSort={currentSort} onSortChange={handleSortChange} />}
        />
        <EventListTemplate
          queryKeyword={keyword}
          districtName={data?.selectedDistrictName}
          serviceOffers={serviceOffers}
          eventTotalLength={total}
          lastIndex={0}
          isMoreData={hasNextPage}
          onClickEvent={handleClickEvent}
          onImpressedEvent={handleImpressedEvent}
          onClickMoreButton={handleClickMoreButton}
          onReachBottom={handleReachBottom}
          discountedCostText={data.defaultText.discountedCostText}
        />
      </TopDownloadScreen>

      {serviceOffers?.map((item) => {
        const {
          id,
          title,
          hospital: { id: hospitalId, name: hospitalName },
          thumbnail,
          representativeOption: {
            price: { originalAmount, discountedAmount, currency, exchangeRateAppliedPrice },
          },
          rating: { amount: ratingAmount, count: ratingCount },
        } = item.serviceOffer

        try {
          const productJsonLd = getHospitalProductJsonLdWithContext({
            name: title,
            description: `${hospitalName}`,
            image: thumbnail,
            priceCurrency: currency,
            price: discountedAmount,
            reviewCount: ratingCount,
            ratingValue: ratingAmount,
          })

          return <JsonLd key={id} data={productJsonLd} />
        } catch (e) {
          console.error(e)
          return <></>
        }
      })}
    </ResponsiveLayout>
  )
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<EventListPageProps>> => {
  const contextEnhancer = new ContextEnhancer(ctx)
  const enhancedContext = (await contextEnhancer.activateAuth()).activateLocale().getContext()

  const { t } = getServerTranslation(enhancedContext.locale)
  const categoryName = ctx.query.category as string
  const districtCode = ctx.query.district as string
  const categoryId = ctx.query.categoryId as string
  const q = ctx.query.q as string
  const sortParam = ctx.query.s as SearchPaginationSort
  const currentSort = sortParam || 'RECOMMENDATION'
  const keyword = categoryName || q || ''

  const country = enhancedContext.country
  const pathnamePrefix = enhancedContext.pathnamePrefix
  const locale = enhancedContext.locale
  const headers = enhancedContext.apiHeaders

  try {
    const treatmentCategoryFilterService = new TreatmentCategoryFilterService(
      createSolarApiClient({ headers: headers as unknown as AxiosRequestHeaders }),
    )
    const [treatmentFilterResponse, districtResponse] = await Promise.allSettled([
      treatmentCategoryFilterService.getTreatmentCategoryFilter(),
      getAllDistricts(headers),
    ])
    const treatmentFilterData: TreatmentFilterResponse | undefined =
      treatmentFilterResponse.status === 'fulfilled' ? treatmentFilterResponse.value : undefined
    const treatmentFilterResponseController: TreatmentFilterResponseController = new TreatmentFilterResponseController(
      treatmentFilterData,
    )
    const selectedTreatmentCategory: TreatmentCategory | undefined =
      treatmentFilterResponseController.getTreatmentCategoryByName(keyword)
    const serviceOfferCategories: TreatmentCategory[] = treatmentFilterResponseController.getTreatmentCategoryList()

    const districtResponseData: DistrictsResponse | undefined =
      districtResponse.status === 'fulfilled' ? districtResponse.value : undefined
    const districtResponseConverter: DistrictResponseController = new DistrictResponseController(districtResponseData)
    const selectedDistrictCodes: string[] | null = districtResponseConverter
      .getTargetDistrictsByCode(districtCode)
      ?.map((district) => district.code)

    const defaultNeighborhoodVicinityCodes = getSearchDistrictCodes(locale, { isCrossCountry: true })

    const selectedDistrictName: string | undefined = districtResponseConverter.getByCode(districtCode)?.name

    const filters = {
      hospital: {
        district: {
          neighborhoodVicinityCodes: selectedDistrictCodes || defaultNeighborhoodVicinityCodes,
        },
      },
    }

    const searchService = new SearchService(
      createSolarApiClient({ headers: headers as unknown as AxiosRequestHeaders }),
    )
    const {
      contents: events,
      totalPageNumber,
      currentPageIndex,
      totalElementNumber,
    } = await searchService.searchServiceOffers({
      keyword,
      filters,
      pagination: {
        pageIndex: 0,
        pageSize: 20,
        sort: currentSort,
      },
    })

    const meta = getMeta(
      locale,
      {
        keyword,
        totalLength: totalElementNumber,
        ...(selectedDistrictName ? { districtName: selectedDistrictName } : {}),
        treatment: selectedTreatmentCategory?.name,
        sort: currentSort,
      },
      {
        canonical: getCanonicalUrl(pathnamePrefix, {
          keyword,
          district: selectedDistrictCodes.length === 1 ? selectedDistrictCodes[0] : districtCode,
          sort: currentSort,
        }),
      },
    )

    /**
     * SEO robot 수집 정책
     * : 검색 결과가 4개 미만일 경우 저평가 페이지로 규정하여 검색 결과를 노출하지 않음.
     */
    if (totalElementNumber < 4) {
      ctx.res.setHeader('x-robots-tag', 'noindex, follow')
    }

    return {
      props: {
        user: enhancedContext.user,
        query: {
          keyword: keyword || '',
          country,
          category: categoryName || '',
          categoryId: categoryId || '',
          q: q || '',
          district: districtCode || '',
          s: currentSort,
          filters,
        },
        locale,
        data: {
          serviceOffers: events,
          serviceOfferPagination: {
            start: currentPageIndex,
            length: totalPageNumber,
            recordsTotal: totalElementNumber,
            recordsFiltered: totalElementNumber,
          },
          serviceOfferCategory: {
            treatmentCategories: serviceOfferCategories,
          },
          defaultText: {
            discountedCostText: t('pages.eventList.contents.discountedCost'),
          },
          ...(selectedDistrictName ? { selectedDistrictName } : {}),
        },
        token: enhancedContext.token,
        meta,
      },
    }
  } catch (e) {
    console.log({ e })
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    }
  }
}
const getCanonicalUrl = (
  pathnamePrefix: PathnamePrefix,
  { keyword, district, sort }: { keyword: string; district: string; sort?: SearchPaginationSort },
) => {
  const host = getEnv('NEXT_PUBLIC_HOST')

  // Fallback to relative URL if host is not available
  if (!host) {
    const params = new URLSearchParams()
    if (keyword) params.set('q', keyword)
    if (district) params.set('district', district)
    if (sort && sort !== 'RECOMMENDATION') params.set('s', sort)

    const queryString = params.toString()
    return `${pathnamePrefix}/events${queryString ? `?${queryString}` : ''}`
  }

  try {
    const canonicalUrl = new URL(`${host}${pathnamePrefix}/events`)
    if (keyword) {
      canonicalUrl.searchParams.set('q', keyword)
    }
    if (district) {
      canonicalUrl.searchParams.set('district', district)
    }
    // Only add sort parameter if it's not the default RECOMMENDATION
    if (sort && sort !== 'RECOMMENDATION') {
      canonicalUrl.searchParams.set('s', sort)
    }
    return canonicalUrl.toString()
  } catch (error) {
    console.warn('Failed to construct canonical URL:', error)
    // Fallback to relative URL
    const params = new URLSearchParams()
    if (keyword) params.set('q', keyword)
    if (district) params.set('district', district)
    if (sort && sort !== 'RECOMMENDATION') params.set('s', sort)

    const queryString = params.toString()
    return `${pathnamePrefix}/events${queryString ? `?${queryString}` : ''}`
  }
}

const getMeta = (
  locale: Locales,
  data: {
    keyword: string
    totalLength: number
    districtName?: string
    treatment?: string
    sort?: SearchPaginationSort
  },
  override?: {
    canonical?: string
  },
): SEOMeta => {
  const commonMeta = getDefaultMeta(locale)
  const year = new Date(Date.now()).getFullYear()

  // Get sorting text for meta tags
  const getSortingText = (sort?: SearchPaginationSort): Record<Locales, string> => {
    if (!sort || sort === 'RECOMMENDATION')
      return {
        [Locales.JA_JP]: '',
        [Locales.EN_US]: '',
        [Locales.TH_TH]: '',
        [Locales.KO_KR]: '',
        [Locales.ZH_HANS_CN]: '',
        [Locales.ZH_HANT_TW]: '',
      }

    const sortTextMap: Record<SearchPaginationSort, Record<Locales, string>> = {
      RECOMMENDATION: {
        [Locales.JA_JP]: '',
        [Locales.EN_US]: '',
        [Locales.TH_TH]: '',
        [Locales.KO_KR]: '',
        [Locales.ZH_HANS_CN]: '',
        [Locales.ZH_HANT_TW]: '',
      },
      LOWEST_DISCOUNTED_COST: {
        [Locales.JA_JP]: '安い順 ',
        [Locales.EN_US]: 'Lowest Price ',
        [Locales.TH_TH]: 'ราคาต่ำสุด ',
        [Locales.KO_KR]: '낮은 가격순 ',
        [Locales.ZH_HANS_CN]: '价格由低到高 ',
        [Locales.ZH_HANT_TW]: '價格由低到高 ',
      },
      HIGHEST_DISCOUNTED_COST: {
        [Locales.JA_JP]: '高い順 ',
        [Locales.EN_US]: 'Highest Price ',
        [Locales.TH_TH]: 'ราคาสูงสุด ',
        [Locales.KO_KR]: '높은 가격순 ',
        [Locales.ZH_HANS_CN]: '价格由高到低 ',
        [Locales.ZH_HANT_TW]: '價格由高到低 ',
      },
      HIGHEST_POPULARITY: {
        [Locales.JA_JP]: '人気順 ',
        [Locales.EN_US]: 'Most Popular ',
        [Locales.TH_TH]: 'ยอดนิยม ',
        [Locales.KO_KR]: '인기순 ',
        [Locales.ZH_HANS_CN]: '热门排序 ',
        [Locales.ZH_HANT_TW]: '熱門排序 ',
      },
      HIGHEST_RATING_AMOUNT: {
        [Locales.JA_JP]: '評価の高い順 ',
        [Locales.EN_US]: 'Highest Rating ',
        [Locales.TH_TH]: 'คะแนนสูงสุด ',
        [Locales.KO_KR]: '높은 평점순 ',
        [Locales.ZH_HANS_CN]: '评分最高 ',
        [Locales.ZH_HANT_TW]: '評分最高 ',
      },
      HIGHEST_RATING_COUNT: {
        [Locales.JA_JP]: 'レビューの多い順 ',
        [Locales.EN_US]: 'Most Reviews ',
        [Locales.TH_TH]: 'รีวิวมากที่สุด ',
        [Locales.KO_KR]: '리뷰 많은순 ',
        [Locales.ZH_HANS_CN]: '评论最多 ',
        [Locales.ZH_HANT_TW]: '評論最多 ',
      },
      HIGHEST_DISCOUNT_RATE: {
        [Locales.JA_JP]: '割引率の高い順 ',
        [Locales.EN_US]: 'Highest Discount ',
        [Locales.TH_TH]: 'ส่วนลดสูงสุด ',
        [Locales.KO_KR]: '높은 할인순 ',
        [Locales.ZH_HANS_CN]: '折扣最高 ',
        [Locales.ZH_HANT_TW]: '折扣最高 ',
      },
      LATEST_STARTS_AT: {
        [Locales.JA_JP]: '新着順 ',
        [Locales.EN_US]: 'Newest ',
        [Locales.TH_TH]: 'ใหม่ล่าสุด ',
        [Locales.KO_KR]: '최신순 ',
        [Locales.ZH_HANS_CN]: '最新排序 ',
        [Locales.ZH_HANT_TW]: '最新排序 ',
      },
      EARLIEST_ENDS_AT: {
        [Locales.JA_JP]: '終了間近順 ',
        [Locales.EN_US]: 'Ending Soon ',
        [Locales.TH_TH]: 'ใกล้สิ้นสุด ',
        [Locales.KO_KR]: '마감 임박순 ',
        [Locales.ZH_HANS_CN]: '',
        [Locales.ZH_HANT_TW]: '',
      },
    }

    return sortTextMap[sort] || sortTextMap.RECOMMENDATION
  }

  const sortText = getSortingText(data.sort)

  const meta: Record<Locales, SEOMeta> = {
    [Locales.JA_JP]: {
      title: `${getPrefixTextByLocale(locale, data)}${sortText[Locales.JA_JP]}おすすめクリニック・値段の比較(${year}) | カンナムオンニ`,
      description: `${getPrefixTextByLocale(locale, data)}施術の値段やおすすめクリニック。 ${data.totalLength}件の美容医療・整形クーポンの口コミと値段をチェック。`,
    },
    [Locales.EN_US]: {
      title: `${getPrefixTextByLocale(locale, data)}${sortText[Locales.EN_US]}Recommended clinics & price comparison (${year}) | Unni`,
      description: `${getPrefixTextByLocale(locale, data)}Check prices for ${data.keyword} and more. ${data.totalLength} reviews are available.`,
    },
    [Locales.TH_TH]: {
      title: `โปรโมชั่น ${data.keyword} & ราคาที่ดีที่สุด | UNNI - แอปเกาหลีอันดับ 1`,
      description: `โปรโมชั่น ${data.keyword} สุดพิเศษ กว่า ${data.totalLength} รออยู่! ดูรีวิวจริง เทียบราคา ปรึกษาและจองคลินิกได้ง่ายๆ กับ UNNI`,
    },
    [Locales.KO_KR]: {
      title: `${getPrefixTextByLocale(locale, data)}${sortText[Locales.KO_KR]}시술추천・가격비교 (${year}) | 강남언니`,
      description: `${getPrefixTextByLocale(locale, data)} 시술 가격 정보 및 추천 리스트. ${data.totalLength}개 병원 시술・성형의 후기와 가격을 확인해보세요. `,
    },
    [Locales.ZH_HANS_CN]: {
      title: `${getPrefixTextByLocale(locale, data)}推荐医院和价格比较 (${year}) | 康南医美`,
      description: `${getPrefixTextByLocale(locale, data)} 检查 ${data.keyword} 和其他的价格。 ${data.totalLength} 条评论可用`,
    },
    [Locales.ZH_HANT_TW]: {
      title: `${getPrefixTextByLocale(locale, data)}推薦醫院和價格比較 (${year}) | 康南醫美`,
      description: `${getPrefixTextByLocale(locale, data)} 檢查 ${data.keyword} 和其他的價格。 ${data.totalLength} 條評論可用`,
    },
  }

  return {
    ...commonMeta,
    ...meta[locale],
    ...override,
  }
}

const getPrefixTextByLocale = (
  locale: Locales,
  data: { keyword: string; districtName?: string; treatment?: string },
) => {
  const prefixTexts: Record<Locales, string> = {
    [Locales.JA_JP]: `${data.treatment ? `${data.treatment} ` : ''}${data.districtName ? `${data.districtName} ` : ''}${data.keyword ? `${data.keyword} ` : ''}`,
    [Locales.EN_US]: `${data.treatment ? `${data.treatment} ` : ''}${data.districtName ? `${data.districtName} ` : ''}${data.keyword ? `${data.keyword} ` : ''}`,
    [Locales.TH_TH]: `${data.treatment ? `${data.treatment} ` : ''}${data.districtName ? `${data.districtName} ` : ''}${data.keyword ? `${data.keyword} ` : ''}`,
    [Locales.KO_KR]: `${data.treatment || data.keyword}${data.districtName ? ` ${data.districtName} ` : ''}`,
    [Locales.ZH_HANS_CN]: `${data.treatment || data.keyword}${data.districtName ? ` ${data.districtName} ` : ''}`,
    [Locales.ZH_HANT_TW]: `${data.treatment || data.keyword}${data.districtName ? ` ${data.districtName} ` : ''}`,
  }
  return prefixTexts[locale]
}

export default EventListPage
